// src/contexts/DocumentCitationTooltipContext.tsx

import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { track } from "../utils/analytics";
import type { CitationTooltipGroup } from "./citationTooltipGroup";
import { wrapIndex } from "./citationTooltipGroup";

/** What the tooltip shows for one document citation. */
export interface DocumentCitationTooltipData {
  documentTitle: string;
  citedText: string;
  location: string;
}

/**
 * State for the global document citation tooltip.
 * Managed at a high level to prevent flickering during streaming.
 */
interface DocumentCitationTooltipState extends DocumentCitationTooltipData {
  isVisible: boolean;
  anchorRect: DOMRect | null;
  /** Set when a merged chip is shown; null for a single citation. */
  group: CitationTooltipGroup<DocumentCitationTooltipData> | null;
  /** Opened by click/Enter on a merged chip: ignores mouse leave until closed. */
  pinned: boolean;
}

/**
 * Context interface for document citation tooltip management
 */
interface DocumentCitationTooltipContextType {
  state: DocumentCitationTooltipState;
  showTooltip: (data: {
    anchorRect: DOMRect | null;
    documentTitle: string;
    citedText: string;
    location: string;
  }) => void;
  /** Show a merged chip's citations, paged. Re-showing the same chip keeps its page. */
  showTooltipGroup: (
    anchorEl: HTMLElement,
    items: DocumentCitationTooltipData[],
    pinned: boolean
  ) => void;
  /** Move the merged chip's tooltip by `delta` citations, wrapping around. */
  pageTooltip: (delta: number) => void;
  /** Hide now, pinned or not. */
  closeTooltip: () => void;
  hideTooltip: () => void;
  /** Keep tooltip visible when mouse enters tooltip area */
  handleTooltipMouseEnter: () => void;
  /** Hide tooltip when mouse leaves tooltip area */
  handleTooltipMouseLeave: () => void;
}

const defaultState: DocumentCitationTooltipState = {
  isVisible: false,
  anchorRect: null,
  documentTitle: "",
  citedText: "",
  location: "",
  group: null,
  pinned: false,
};

const DocumentCitationTooltipContext = createContext<
  DocumentCitationTooltipContextType | undefined
>(undefined);

interface DocumentCitationTooltipProviderProps {
  children: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

/**
 * Provider for document citation tooltip state.
 * Place at a high level (e.g., ChatMessages) so tooltip never unmounts during streaming.
 */
export const DocumentCitationTooltipProvider: React.FC<DocumentCitationTooltipProviderProps> = ({
  children,
  scrollContainerRef,
}) => {
  const [state, setState] = useState<DocumentCitationTooltipState>(defaultState);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const analyticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleRef = useRef(false);
  const pinnedRef = useRef(false);
  const groupRef = useRef<CitationTooltipGroup<DocumentCitationTooltipData> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const clearAnalyticsTimeout = useCallback(() => {
    if (analyticsTimeoutRef.current) {
      clearTimeout(analyticsTimeoutRef.current);
      analyticsTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(
    (data: {
      anchorRect: DOMRect | null;
      documentTitle: string;
      citedText: string;
      location: string;
    }) => {
      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = true;
      pinnedRef.current = false;
      groupRef.current = null;
      setState({
        isVisible: true,
        anchorRect: data.anchorRect,
        documentTitle: data.documentTitle,
        citedText: data.citedText,
        location: data.location,
        group: null,
        pinned: false,
      });
      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "document",
          document_title: data.documentTitle,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearHideTimeout, clearAnalyticsTimeout]
  );

  // One view per page a reader dwells on, tagged with its place in the group.
  const trackGroupPageViewed = useCallback(
    (group: CitationTooltipGroup<DocumentCitationTooltipData>) => {
      clearAnalyticsTimeout();
      const item = group.items[group.index];

      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "document",
          document_title: item.documentTitle,
          group_size: group.items.length,
          group_index: group.index,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearAnalyticsTimeout]
  );

  const showTooltipGroup = useCallback(
    (anchorEl: HTMLElement, items: DocumentCitationTooltipData[], pinned: boolean) => {
      if (items.length === 0) return;
      clearHideTimeout();
      const current = groupRef.current;
      const sameChip = isVisibleRef.current && current?.anchorEl === anchorEl;
      const group = {
        items,
        index: sameChip && current ? Math.min(current.index, items.length - 1) : 0,
        anchorEl,
      };
      const nextPinned = pinned || (sameChip && pinnedRef.current);

      isVisibleRef.current = true;
      pinnedRef.current = nextPinned;
      groupRef.current = group;
      setState({
        isVisible: true,
        anchorRect: anchorEl.getBoundingClientRect(),
        ...items[group.index],
        group,
        pinned: nextPinned,
      });
      if (!sameChip) trackGroupPageViewed(group);
    },
    [clearHideTimeout, trackGroupPageViewed]
  );

  const pageTooltip = useCallback(
    (delta: number) => {
      const current = groupRef.current;

      if (!current || !isVisibleRef.current) return;
      const group = { ...current, index: wrapIndex(current.index + delta, current.items.length) };

      groupRef.current = group;
      setState((prev) => ({ ...prev, ...group.items[group.index], group }));
      trackGroupPageViewed(group);
    },
    [trackGroupPageViewed]
  );

  const closeTooltip = useCallback(() => {
    clearHideTimeout();
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    pinnedRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
  }, [clearHideTimeout, clearAnalyticsTimeout]);

  const hideTooltip = useCallback(() => {
    if (pinnedRef.current) return;
    clearAnalyticsTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      isVisibleRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false }));
    }, 100);
  }, [clearAnalyticsTimeout]);

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    if (pinnedRef.current) return;
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false }));
  }, [clearAnalyticsTimeout]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      clearAnalyticsTimeout();
    };
  }, [clearAnalyticsTimeout]);

  // Hide tooltip on scroll
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!isVisibleRef.current) return;

      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = false;
      pinnedRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef, clearHideTimeout]);

  return (
    <DocumentCitationTooltipContext.Provider
      value={{
        state,
        showTooltip,
        showTooltipGroup,
        pageTooltip,
        closeTooltip,
        hideTooltip,
        handleTooltipMouseEnter,
        handleTooltipMouseLeave,
      }}
    >
      {children}
    </DocumentCitationTooltipContext.Provider>
  );
};

/**
 * Hook to access document citation tooltip context.
 * Must be used within DocumentCitationTooltipProvider.
 */
export const useDocumentCitationTooltip = (): DocumentCitationTooltipContextType => {
  const context = useContext(DocumentCitationTooltipContext);
  if (!context) {
    throw new Error(
      "useDocumentCitationTooltip must be used within DocumentCitationTooltipProvider"
    );
  }
  return context;
};

export default DocumentCitationTooltipContext;
